import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UserDetailModel } from '@app/core/models/users/user-detail.model';
import { UserUpdateModel } from '@app/core/models/users/user-update.model';
import { ConfigService } from '@core/config/config.service';
import { AuthService } from '@core/guards/auth.service';
import { HttpService } from '@core/http/http.service';
import { ImportReportModel } from '@core/models/administration/imports/import-report.model';
import { GroupModel } from '@core/models/groups/group.model';
import { UserGroupModel } from '@core/models/groups/user-group-model';
import { PagedResult } from '@core/models/paged-result.model';
import { PagingParametersModel } from '@core/models/shared/paging-parameters.model';
import { UserDashboardModel } from '@core/models/users/user-dashboard.model';
import { UserProfileModel } from '@core/models/users/user-profile.model';
import { UserModel } from '@core/models/users/user.model';
import { Observable } from 'rxjs';
import { GroupTypeModel } from '../models/groups/group-type.model';

@Injectable({
  providedIn: 'root',
})
export class UserService extends HttpService {
  constructor(
    protected http: HttpClient,
    protected config: ConfigService,
    protected authService: AuthService
  ) {
    super(http, config, authService, 'users');
  }

  getGroups(groupsType?: GroupTypeModel[]): Observable<GroupModel[]> {
    return this.invokeGet<GroupModel[]>(`groups`, { groupsType: groupsType });
  }

  getUserGroups(
    userId: string,
    sort?: string,
    order?: string,
    page?: number,
    size?: number
  ): Observable<PagedResult<UserGroupModel>> {
    const pagingParameters = new PagingParametersModel(
      sort,
      order,
      page + 1,
      size
    );
    const userGroups = this.invokeGet<PagedResult<UserGroupModel>>(
      `${userId}/groups`,
      null,
      pagingParameters
    );
    return userGroups;
  }

  getAllUserGroups(userId: string): Observable<UserGroupModel[]> {
    return this.invokeGet<UserGroupModel[]>(`${userId}/allgroups`);
  }

  getUsers(): Observable<UserModel[]> {
    return this.invokeGet<UserModel[]>();
  }

  getUserById(userId: string): Observable<UserModel> {
    return this.getById(userId);
  }

  getuserDetailById(userId: string): Observable<UserDetailModel> {
    return this.invokeGet<UserDetailModel>(`${userId}/detail`);
  }

  getUserProfile(): Observable<UserProfileModel> {
    return this.invokeGet<UserProfileModel>(`profile`);
  }

  deleteUserProfileCache(userId: string): Observable<Boolean> {
    return this.invokeDelete(`profile/cache/${userId}`);
  }

  getUserDashboard(): Observable<UserDashboardModel> {
    return this.invokeGet<UserDashboardModel>(`dashboard`);
  }

  deactivateUser(userId: string): Observable<any> {
    return this.invokePut(null, `${userId}/deactivate`);
  }

  activateUser(userId: string): Observable<any> {
    return this.invokePut(null, `${userId}/activate`);
  }

  createUser(user: UserDetailModel): Observable<any> {
    return this.invokePost(user);
  }

  updateUser(user: UserUpdateModel): Observable<any> {
    return this.invokePut(user);
  }

  getActiveDirectoryUsers(searchCriteria: string): Observable<UserModel[]> {
    return this.invokeGet<UserModel[]>(
      `activedirectory/search/${searchCriteria}`
    );
  }

  importUsers(files: File[]): Observable<ImportReportModel[]> {
    return this.invokeUploadFiles(files, `import`);
  }
}
